import { AMENITY_TYPE_PRIORITY } from "constants/index";
import sortedTagsList from "./constants";

export const getTags = (data: any) => {
  const tags: any = [];
  data?.forEach((d: any) => {
    if (d?.display_name) {
      tags.push(d.display_name);
    }
  });
  return tags;
};

export const getFeatures = (data: any) => {
  const tags: any = [];
  data?.forEach((d: any) => {
    if (d?.name) {
      tags.push(d.name);
    }
  });
  return tags;
};

// export const getTopAmenitySection = (data: any, forEn = false) => {
//   for (const type of AMENITY_TYPE_PRIORITY) {
//     const found = data?.find((d: any) => (forEn ? d?.type_en : d?.type) === type);
//     if (found) return found;
//   }
//   return null;
// };

export const getTopAmenitySections = (data: any, count = 2, forEn = false) => {
  const results: any[] = [];
  for (const type of AMENITY_TYPE_PRIORITY) {
    if (results.length >= count) break;
    const found = data?.find((d: any) => (forEn ? d?.type_en ?? d?.type : d?.type) === type);
    if (found && !results.includes(found)) results.push(found);
  }
  return results;
};

export const sortAmenitySections = (data: any, forEn = false) =>
  [...(data || [])].sort((a: any, b: any) => {
    const typeA = forEn ? a?.type_en ?? a?.type : a?.type;
    const typeB = forEn ? b?.type_en ?? b?.type : b?.type;
    const indexA = AMENITY_TYPE_PRIORITY.indexOf(typeA);
    const indexB = AMENITY_TYPE_PRIORITY.indexOf(typeB);
    // Items not in priority list go to end
    if (indexA === -1 && indexB === -1) return 0;
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;
    return indexA - indexB;
  });

const FREE_STANDALONE_REGEX = /^free(?:[\s-]|$)/i;

const BILLS_INCLUDED_SECTION = "Bills Included";

/**
 * A tag carried as a display/English pair so both language arrays can be built
 * in a single pass and stay index-aligned. InventoryDetailTags pairs the text
 * (display) with the icon (English) by index, so any divergence between the two
 * arrays shows up as mismatched icons/labels — most visibly in the China build
 * where `name` is localized and only `name_en` matches English keywords.
 */
export type TagPair = { display: string; en: string };

const toTagPair = (value: any): TagPair | null => {
  const en = value?.name_en || value?.name;
  const display = value?.name || value?.name_en;
  if (!display || !en) return null;
  return { display, en };
};

const isBillsIncludedSection = (section: any) =>
  (section?.name_en || section?.name) === BILLS_INCLUDED_SECTION;

/**
 * Free amenity tags from every section (including Bills Included).
 * Matching is always done on the English name so the same items are selected
 * in every locale.
 */
export const extractFreeTagPairs = (features: any = []): TagPair[] => {
  const freeTags: TagPair[] = [];
  features?.forEach((section: any) => {
    section?.values?.forEach((value: any) => {
      const pair = toTagPair(value);
      if (pair && FREE_STANDALONE_REGEX.test(pair.en)) {
        freeTags.push(pair);
      }
    });
  });
  return freeTags;
};

/** Individual bill items from the "Bills Included" section. */
export const extractBillsIncludedTagPairs = (features: any = []): TagPair[] => {
  const billsSection = features?.find(isBillsIncludedSection);
  if (!billsSection?.values) return [];
  return billsSection.values
    .map(toTagPair)
    .filter((pair: TagPair | null): pair is TagPair => Boolean(pair));
};

/** Lowercased English names — the language-agnostic key used for exclusion. */
export const buildExcludeSet = (...pairGroups: TagPair[][]): Set<string> =>
  new Set(
    pairGroups
      .flat()
      .map((pair) => pair?.en?.toLowerCase())
      .filter(Boolean),
  );

/**
 * Drops tags that duplicate an earlier one, comparing on the English name so
 * the result is identical across locales. A tag counts as a duplicate when its
 * English name equals, contains, or is contained by an already-kept tag
 * (e.g. "Laundry Facility" vs "Laundry"). Mirrors getUniqueTagIndices in
 * InventoryDetailTags.
 */
export const dedupeTagPairs = (pairs: TagPair[]): TagPair[] =>
  pairs.reduce((kept: TagPair[], pair) => {
    const en = pair?.en?.toLowerCase();
    if (!en) return kept;
    const isDuplicate = kept.some((keptPair) => {
      const keptEn = keptPair.en.toLowerCase();
      return keptEn === en || en.includes(keptEn) || keptEn.includes(en);
    });
    if (!isDuplicate) kept.push(pair);
    return kept;
  }, []);

export const getAmenities = (
  tags: any = [],
  data: any,
  forEn = false,
  excludeSet?: Set<string>,
  // Only the new priority path skips this section (it emits those items itself
  // via extractBillsIncludedTagPairs). Left off so the legacy path is unchanged.
  skipBillsIncludedSection = false,
) => {
  const tagsLower = tags.map((t: string) => t?.toLowerCase());
  // Group amenities by type
  const amenitiesByType: Record<string, any[]> = {};
  data?.forEach((d: any) => {
    const type = d?.type_en ?? d?.type;
    if (skipBillsIncludedSection && isBillsIncludedSection(d)) return;
    if (AMENITY_TYPE_PRIORITY.includes(type)) {
      d?.values?.forEach((tag: any) => {
        const tagName = forEn ? tag?.name_en : tag?.name;
        const lowerName = tagName?.toLowerCase();
        // Always key exclusion on the English name so the display and English
        // passes drop the same items and stay index-aligned.
        const lowerEnName = (tag?.name_en || tag?.name)?.toLowerCase();
        if (
          !tagsLower.includes(lowerName) &&
          !(excludeSet && lowerEnName && excludeSet.has(lowerEnName))
        ) {
          if (!amenitiesByType[type]) amenitiesByType[type] = [];
          amenitiesByType[type].push(tagName);
        }
      });
    }
  });
  // Flatten in priority order
  let newTags: any[] = [];
  AMENITY_TYPE_PRIORITY.forEach((type: any) => {
    if (amenitiesByType[type]) {
      newTags = newTags.concat(amenitiesByType[type]);
    }
  });
  return tags ? [...tags, ...newTags] : newTags;
};

export const getCommonAmenities = getAmenities;

export const executeScrollToPosition = ({ node, offset, scrollingElement = window }: any) => {
  if (!node) return;
  const elementPosition = node.getBoundingClientRect().top;
  const offsetPosition = elementPosition + window.pageYOffset - offset;
  scrollingElement.scrollTo({
    top: offsetPosition,
    behavior: "smooth",
  });
};

export const getShowPropertyInfoTags = (propertyInfoTags: any) => {
  let value = false;
  if (propertyInfoTags) {
    propertyInfoTags.forEach((tag: any) => {
      if (tag?.value > 0) value = true;
    });
  }
  return value;
};

export const getSortedTags = (tagList: any) =>
  tagList
    ?.filter((tag: string) => tag !== "not_available") // Remove not available tag used by CRM
    ?.map((tag: string) => tag?.replace(/_/g, " ")) // Remove underscores
    ?.slice() // To prevent mutation
    // Sort according to provided sorted list array
    ?.sort((a: any, b: any) => {
      // Move items not present to end
      if (sortedTagsList.indexOf(b?.toLowerCase()) === -1) {
        return -1;
      }
      if (sortedTagsList.indexOf(a?.toLowerCase()) === -1) {
        return 1;
      }
      // Sort according to list provided
      return sortedTagsList?.indexOf(a?.toLowerCase()) - sortedTagsList?.indexOf(b?.toLowerCase());
    }) || [];

export const milesToKilometersConverter = (distance: any) => {
  const distanceStr = distance?.toString()?.toLowerCase();
  const isMiles = distanceStr?.includes("mi");
  const isFeet = distanceStr?.includes("ft") || distanceStr?.includes("feet");
  const numericValue = parseFloat(distanceStr?.replace(/[^\d.]/g, ""));

  let kilometers = 0;

  if (isMiles) {
    kilometers = numericValue * 1.60934;
  } else if (isFeet) {
    kilometers = numericValue * 0.0003048;
  } else {
    kilometers = numericValue;
  }
  if (kilometers < 0.1) {
    kilometers = 0.1;
  }
  return `${kilometers.toFixed(1)} km`;
};

/* -------------------------------------------------------------------------- */
/*                        Overview tag ordering (gated)                       */
/* -------------------------------------------------------------------------- */
/**
 * Tag ordering for the property page overview scroll and the search page
 * inventory cards.
 *
 * Both builders below take an `isPriorityEnabled` flag (driven by the
 * GROWTH_BOOK_FEATURE_FLAG_NAMES.PROPERTY_DETAIL_TAGS_PRIORITY GrowthBook flag):
 *
 * - flag ON  → new priority order, deduplicated:
 *     1. CRM tags (feature_tags)
 *     2. Starting tags (tags prop)
 *     3. Free amenity tags  ("Free" as a standalone word, any section)
 *     4. Bills Included items
 *     5. Remaining amenity section tags (AMENITY_TYPE_PRIORITY order)
 * - flag OFF → previous behaviour, untouched.
 */

/** Builds the ordered tag list for the property page overview scroll. */
export const buildPropertyDetailTags = ({
  tagsArray = [],
  tags,
  features,
  mapTags = {},
  isPriorityEnabled = false,
}: {
  tagsArray?: any[];
  tags?: any;
  features?: any;
  mapTags?: Record<string, string>;
  isPriorityEnabled?: boolean;
}): string[] => {
  // Old behaviour — CRM tags followed by amenities, no free/bills promotion.
  if (!isPriorityEnabled) {
    return tagsArray?.concat(getAmenities(getSortedTags(tags || []), features || []));
  }

  // Matched on the English name so the same items are promoted in every locale,
  // including the China build where `name` is localized.
  const freeTagPairs = extractFreeTagPairs(features || []);
  const billsIncludedTagPairs = extractBillsIncludedTagPairs(features || []);
  const excludeSet = buildExcludeSet(freeTagPairs, billsIncludedTagPairs);

  const sortedStartingTags = getSortedTags(tags || []);
  const amenitySectionTags = getAmenities(
    sortedStartingTags,
    features || [],
    false,
    excludeSet,
    true,
  );

  // getAmenities returns [...startingTags, ...remainingAmenities]
  const startingTagsCount = (sortedStartingTags || []).length;
  const startingTags = amenitySectionTags.slice(0, startingTagsCount);
  const remainingAmenityTags = amenitySectionTags.slice(startingTagsCount);

  const toPair = (tag: string): TagPair => ({ display: tag, en: mapTags[tag] || tag });

  return dedupeTagPairs([
    ...tagsArray.map(toPair), // 1. CRM tags
    ...startingTags.map(toPair), // 2. Starting tags
    ...freeTagPairs, // 3. Free amenity tags
    ...billsIncludedTagPairs, // 4. Bills Included items
    ...remainingAmenityTags.map(toPair), // 5. Remaining amenities
  ]).map((pair) => pair.display);
};

/**
 * Builds the ordered tag lists for a search page inventory card.
 *
 * Returns the display and English arrays together because InventoryDetailTags
 * pairs them by index — the label comes from `updatedTags`, the icon from
 * `updatedTagsEn`. Building both from one ordered pair list keeps that
 * alignment structural instead of incidental.
 */
export const buildSearchListTags = ({
  tagsArray = [],
  sortedTags = [],
  sortedTagsEn = [],
  features,
  isPriorityEnabled = false,
}: {
  tagsArray?: any[];
  sortedTags?: any[];
  sortedTagsEn?: any[];
  features?: any;
  isPriorityEnabled?: boolean;
}): { updatedTags: string[]; updatedTagsEn: string[] } => {
  // Old behaviour — CRM tags followed by amenities, no free/bills promotion.
  if (!isPriorityEnabled) {
    const updatedTags = tagsArray?.concat(getCommonAmenities(sortedTags, features));
    const rawUpdatedTagsEn = tagsArray?.concat(getCommonAmenities(sortedTagsEn, features, true));
    return {
      updatedTags,
      updatedTagsEn: updatedTags?.map((tag: string, i: number) => rawUpdatedTagsEn?.[i] ?? tag),
    };
  }

  const freeTagPairs = extractFreeTagPairs(features || []);
  const billsIncludedTagPairs = extractBillsIncludedTagPairs(features || []);
  const excludeSet = buildExcludeSet(freeTagPairs, billsIncludedTagPairs);

  const amenitySectionTags = getCommonAmenities(
    sortedTags,
    features || [],
    false,
    excludeSet,
    true,
  );
  const amenitySectionTagsEn = getCommonAmenities(
    sortedTagsEn,
    features || [],
    true,
    excludeSet,
    true,
  );

  // getCommonAmenities returns [...startingTags, ...remainingAmenities]
  const sortedTagsCount = (sortedTags || []).length;
  const toPairs = (display: string[], en: string[]): TagPair[] =>
    (display || []).map((tag: string, i: number) => ({ display: tag, en: en?.[i] ?? tag }));

  const startingTagPairs = toPairs(
    amenitySectionTags.slice(0, sortedTagsCount),
    amenitySectionTagsEn.slice(0, sortedTagsCount),
  );
  const remainingAmenityTagPairs = toPairs(
    amenitySectionTags.slice(sortedTagsCount),
    amenitySectionTagsEn.slice(sortedTagsCount),
  );

  const orderedTagPairs = dedupeTagPairs([
    ...tagsArray.map((tag: string) => ({ display: tag, en: tag })), // 1. CRM tags
    ...startingTagPairs, // 2. Starting tags
    ...freeTagPairs, // 3. Free amenity tags
    ...billsIncludedTagPairs, // 4. Bills Included items
    ...remainingAmenityTagPairs, // 5. Remaining amenities
  ]);

  return {
    updatedTags: orderedTagPairs.map((pair) => pair.display),
    updatedTagsEn: orderedTagPairs.map((pair) => pair.en),
  };
};
